import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import CommandPalette from '../components/CommandPalette';
import CaseSlidePanel from '../components/CaseSlidePanel';
import DemoBanner from '../components/DemoBanner';
import OnboardingWizard from '../components/onboarding/OnboardingWizard';
import { auth } from '../utils/auth';
import { useDarkMode } from '../context/DarkModeContext';

const LexyAppShell = ({ children, activeView, onNavigate, onOpenGuide }: { children: any; activeView: any; onNavigate?: any; onOpenGuide?: any }) => {
  const navigate = useNavigate();
  const { isDarkMode } = useDarkMode();
  const user = auth.getUser();
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [showDemo, setShowDemo] = useState(false);
  // Check sandbox status from localStorage (set during signup)
  const isSandbox = localStorage.getItem('is_sandbox') === 'true';

  useEffect(() => {
    // Allow LAWYER, ADMIN, and TENANT_ADMIN to access Lexy
    const allowedRoles = ['LAWYER', 'ADMIN', 'TENANT_ADMIN'];
    if (!user || !allowedRoles.includes(user.role)) {
      navigate('/lexy');
      return;
    }

    // Global keyboard shortcuts
    const handleKeyDown = (e) => {
      // Command Palette (⌘K or Ctrl+K)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(true);
      }

      // Close panels on ESC
      if (e.key === 'Escape') {
        if (commandPaletteOpen) {
          setCommandPaletteOpen(false);
        } else if (selectedCase) {
          setSelectedCase(null);
        }
      }

      // Navigation shortcuts
      if (e.metaKey || e.ctrlKey) {
        switch(e.key) {
          case 'h':
            e.preventDefault();
            if (onNavigate) onNavigate('overview');
            break;
          case 'l':
            e.preventDefault();
            if (onNavigate) onNavigate('cases');
            break;
          case 'm':
            e.preventDefault();
            if (onNavigate) onNavigate('marketplace');
            break;
          case 'b':
            e.preventDefault();
            setSidebarExpanded(!sidebarExpanded);
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [commandPaletteOpen, selectedCase, onNavigate, sidebarExpanded]);

  const mobileMenuItems = [
    { id: 'overview', icon: 'Home', label: 'Hjem' },
    { id: 'cases', icon: 'FileText', label: 'Saker' },
    { id: 'marketplace', icon: 'Sparkles', label: 'Marked' },
    { id: 'settings', icon: 'Settings', label: 'Mer' },
  ];

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-950' : 'bg-white'}`}>
      {/* Demo Banner - Only show in sandbox */}
      {isSandbox && <DemoBanner onOpenGuide={onOpenGuide} />}

      {/* Sidebar - Hidden on mobile */}
      <Sidebar 
        activeView={activeView} 
        onNavigate={onNavigate}
        isExpanded={sidebarExpanded}
        onToggle={() => setSidebarExpanded(!sidebarExpanded)}
        onShowDemo={() => setShowDemo(true)}
      />

      {/* Main Content Area */}
      <div className={`h-screen transition-all duration-300 pb-16 md:pb-0 overflow-hidden ${sidebarExpanded ? 'md:ml-56' : 'md:ml-14'}`}>
        {/* Content wrapper with smooth transitions */}
        <div className="h-full overflow-y-auto overflow-x-hidden">
          {children}
        </div>
      </div>

      {/* Mobile Bottom Navigation - Only visible on mobile */}
      <nav className={`md:hidden fixed bottom-0 left-0 right-0 border-t z-40 ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
        <div className="grid grid-cols-4 h-16">
          {[
            { id: 'overview', icon: '🏠', label: 'Hjem' },
            { id: 'cases', icon: '📋', label: 'Saker' },
            { id: 'marketplace', icon: '✨', label: 'Marked' },
            { id: 'settings', icon: '⚙️', label: 'Mer' },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => onNavigate && onNavigate(item.id)}
              className={`flex flex-col items-center justify-center gap-1 transition-colors ${
                activeView === item.id
                  ? isDarkMode
                    ? 'text-white bg-gray-800'
                    : 'text-gray-900 bg-gray-50'
                  : isDarkMode
                  ? 'text-gray-400'
                  : 'text-gray-500'
              }`}
            >
              <span className="text-xl">{item.icon}</span>
              <span className="text-xs font-medium">{item.label}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Command Palette */}
      <CommandPalette
        isOpen={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
        onSelectCase={(caseId) => {
          setSelectedCase(caseId);
          setCommandPaletteOpen(false);
        }}
      />

      {/* Case Slide Panel */}
      <CaseSlidePanel
        caseId={selectedCase}
        isOpen={!!selectedCase}
        onClose={() => setSelectedCase(null)}
      />

      {/* Onboarding Wizard */}
      {showDemo && user && (
        <OnboardingWizard
          user={user}
          onComplete={() => setShowDemo(false)}
          onSkip={() => setShowDemo(false)}
        />
      )}
    </div>
  );
};

export default LexyAppShell;
